/**
 * Unified Social data model.
 * Posts belong to the Owner account; pets are tagged per post (optional).
 */

export interface TaggedPetSummary {
  id: string;
  name: string;
  avatar: string;
  species?: string;
}

export interface SocialComment {
  id: string;
  authorId?: string;
  author: string;
  avatar: string;
  text: string;
  timestamp: string;
}

/** External platforms where a post was shared (cross-post) */
export type SharedPlatform = 'instagram' | 'facebook' | 'tiktok' | 'x';

export interface SocialPost {
  id: string;
  ownerId: string;
  ownerName: string;
  ownerAvatar?: string;
  /** Pet IDs tagged in this post (metadata); empty = owner-only post */
  taggedPetIds: string[];
  /** Resolved pet display info for feed (id, name, avatar) */
  taggedPets: TaggedPetSummary[];
  timestamp: string;
  images: string[];
  caption: string;
  likes: number;
  comments: SocialComment[];
  isLiked: boolean;
  isSaved: boolean;
  location?: string;
  fromPetParks?: boolean;
  tags: string[];
  professionalBadge?: string;
  verified?: boolean;
  /** Platforms this post was cross-posted to (for display and analytics) */
  sharedPlatforms?: SharedPlatform[];
  /** When the post was created (ms since epoch); used for 1-hour edit window */
  createdAt?: number;
  /** When the post was last edited (ms since epoch) */
  editedAt?: number;
  /** History of edits: timestamp (and optional editor) for each edit */
  editHistory?: { editedAt: number; editedBy?: string }[];
}

export interface PetForSocial {
  id: string;
  name: string;
  species?: string;
  avatar: string;
  imageUrl?: string;
  photoKey?: string;
}

/**
 * Build taggedPets display array from taggedPetIds using owner's pets list.
 */
export function resolveTaggedPets(
  taggedPetIds: string[],
  pets: PetForSocial[]
): TaggedPetSummary[] {
  if (!taggedPetIds.length) return [];
  return taggedPetIds
    .map((id) => {
      const pet = pets.find((p) => p.id === id);
      if (!pet) return null;
      return {
        id: pet.id,
        name: pet.name,
        avatar:
          pet.avatar ||
          (pet.species === 'dog' ? '🐕' : pet.species === 'cat' ? '🐱' : '🐾'),
        species: pet.species,
      };
    })
    .filter((p): p is TaggedPetSummary => p != null);
}
